import React, { useEffect, useState } from 'react';
import ActionButton from '../buttons/ActionButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlusSquare,
  faSave,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';

import 'view/style/common/components/inputTable.css';
import { FileInput } from '../teacher/assignment/FileInputs';

type InputTableProps<T> = {
  headers: Array<{ key: keyof T; label: string; type: string }>;
  importRows?: T[];
  size?: number;
  maxRows?: number;
  onSubmit?: (rows: T[]) => Promise<boolean[]>;
};

const InputTable = <T extends Record<string, any>>({
  headers,
  size = 1,
  maxRows,
  importRows,
  onSubmit,
}: InputTableProps<T>) => {
  const [rows, setRows] = useState<T[]>(
    Array.from({ length: size }, () =>
      headers.reduce((acc, { key }) => ({ ...acc, [key]: '' }), {} as T),
    ),
  );

  // When parent passes importRows, use them
  useEffect(() => {
    if (importRows) {
      setRows(importRows);
    }
  }, [importRows]);

  // --- CSV import handler ---
  const handleCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target?.result as string;
      const lines = text.trim().split(/\r\n|\n/);
      if (lines.length <= 1) return;

      // parse header row
      const csvCols = lines[0].split(',').map((h) => h.trim());
      const requiredCols = headers.map(({ key }) => String(key));
      const missingCols = requiredCols.filter((col) => !csvCols.includes(col));
      if (missingCols.length > 0) {
        alert(
          `Invalid CSV format.\n` +
            `Required columns: ${requiredCols.join(', ')}\n` +
            `Missing: ${missingCols.join(', ')}`,
        );
        return;
      }
      // map remaining rows
      const parsed: T[] = lines
        .slice(1)
        .map((line) => {
          const values = line.split(',').map((v) => v.trim());
          const temp: any = {};
          let valid = true;

          headers.forEach(({ key }) => {
            const colIndex = csvCols.indexOf(String(key));
            // if any expected column is missing, mark invalid
            if (colIndex < 0 || colIndex >= values.length) {
              valid = false;
            } else {
              temp[key] = values[colIndex];
            }
          });

          // only return a T when every key was found
          return valid ? (temp as T) : null;
        })
        .filter((row): row is T => row !== null);

      setRows(parsed);
    };
    reader.readAsText(file);
  };
  // ----------------------------

  const addRow = () => {
    if (maxRows && rows.length >= maxRows) return;
    setRows([
      ...rows,
      headers.reduce((acc, { key }) => ({ ...acc, [key]: '' }), {} as T),
    ]);
  };

  const deleteRow = (index: number) => {
    let updated = rows.filter((_, i) => i !== index);
    if (updated.length === 0) {
      updated = [
        headers.reduce((acc, { key }) => ({ ...acc, [key]: '' }), {} as T),
      ];
    }
    setRows(updated);
  };

  const updateCell = (rowIndex: number, key: keyof T, value: string) => {
    setRows((prev) =>
      prev.map((row, i) => (i === rowIndex ? { ...row, [key]: value } : row)),
    );
  };

  const handleSubmit = () => {
    if (!onSubmit) return;
    onSubmit(rows).then((results) =>
      setRows((prev) => prev.filter((_, i) => !results[i])),
    );
  };

  return (
    <>
      {/* CSV import */}
      <FileInput
        label="Import from CSV"
        accept=".csv"
        type="inline"
        onFileChange={handleCsvUpload}
      />

      {/* Data table */}
      <div className="input-table-container">
        <table className="input-table">
          <thead>
            <tr>
              {headers.map((h, i) => (
                <th key={i}>{h.label}</th>
              ))}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri}>
                {headers.map(({ key, type }) => (
                  <td key={String(key)}>
                    <input
                      type={type}
                      value={row[key] ?? ''}
                      onChange={(e) => updateCell(ri, key, e.target.value)}
                    />
                  </td>
                ))}
                <td onClick={() => deleteRow(ri)}>
                  <FontAwesomeIcon icon={faTrash} color="var(--dark-pink)" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Actions */}
      <div className="row">
        <ActionButton
          type="edit"
          label="Add Row"
          icon={faPlusSquare}
          onClick={addRow}
          disabled={!!maxRows && rows.length >= maxRows}
        />
        {onSubmit && (
          <ActionButton
            type="go"
            label="Submit"
            icon={faSave}
            onClick={handleSubmit}
          />
        )}
      </div>
    </>
  );
};

export default InputTable;
